import type { Spell } from '../../schemas/spell';

/**
 * Spell list del Mago — Trucchetti (L0) + Livelli 1, 3, 5, 7, 9 (manuale V1,
 * sezione "Lista degli Incantesimi" rr. 6219+, indice classe-→-spell a
 * r. 6216).
 *
 * **Scope**:
 * - 3 trucchetti L0 (auto-aggiunti dal *Corpus Arcano* iniziale)
 * - 8 incantesimi L1 (il PG ne sceglie 3 alla creazione)
 * - 7 incantesimi L3 (accessibili al PG Mago di livello ≥ 3 via
 *   `Corpus Arcano` ricorrente o `Magnus Corpus Arcano` come grant on-take)
 * - 7 incantesimi L5 (accessibili dal livello 5)
 * - **7 incantesimi L7** (D4 — accessibili dal livello 7)
 * - **5 incantesimi L9** (D4 — accessibili dal livello 9)
 *
 * **`availableTo` esteso**: gli spell condivisi includono entry per
 * Chierico (Tradizioni rilevanti) / Druido / Warlock / Paladino dove
 * il manuale lo prevede. Pattern additivo, niente refactor degli ID.
 *
 * **Note D4**:
 * - *Separazione* (L7): la tabella indice del manuale (r. 6216, parsing
 *   `.md`) la elenca sotto «Livello 9» per errore di allineamento delle
 *   colonne; la description canonica (r. 6569) e il PDF chiariscono che
 *   è **L7**. Verificato col PDF inviato 2026-05-13.
 * - *Tempesta Arcana* (L9): description completa allineata al manuale V1
 *   r. 6594 (gap iniziale chiuso 2026-05-15 con aggiornamento manuale).
 *
 * **Note D11** (spell catalog completion):
 * - *Luce* (L0): `availableTo` esteso con `paladino-negromanzia` e
 *   `paladino-valore` per simmetria con Chierico-Negromanzia/Valore L0
 *   (manuale V1 r. 6204 e r. 6209). Paladino-Natura(Elfo) era già presente.
 *
 * Convenzione ID: `mago.<kebab-name>`.
 */

// ============================================================================
// Trucchetti (Livello 0) — auto-aggiunti al PG Mago L1
// ============================================================================

const luce: Spell = {
  id: 'mago.luce',
  name: 'Luce',
  level: 0,
  tags: ['continuato'],
  description:
    "Un oggetto che tocchi brilla di luce intensa più o meno come quella di una torcia. Non emette né calore né suono, non richiede combustibile, e hai il totale controllo sul suo colore. L'effetto dura finché l'oggetto rimane in tua presenza, a meno che tu non decida di porvi fine.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'chierico', tradizione: 'crepuscolo' },
    { classId: 'chierico', tradizione: 'guarigione' },
    { classId: 'chierico', tradizione: 'illuminazione' },
    { classId: 'chierico', tradizione: 'negromanzia' },
    { classId: 'chierico', tradizione: 'valore' },
    { classId: 'druido' },
    // Paladino-Ordine Sacro accede a tutti gli incantesimi delle proprie
    // Tradizioni Sacre (manuale V1, r. 6184). *Luce* compare nelle colonne
    // Orazioni L0 di Negromanzia (r. 6204) e Valore (r. 6209).
    { classId: 'paladino', tradizione: 'negromanzia' },
    { classId: 'paladino', tradizione: 'valore' },
    // Tradizione Natura del Paladino (Elfo) attinge alla lista del Druido,
    // che include *Luce*.
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
  ],
};

const prestidigitazione: Spell = {
  id: 'mago.prestidigitazione',
  name: 'Prestidigitazione',
  level: 0,
  tags: [],
  description:
    "Se al momento del lancio tocchi un oggetto inanimato puoi apportarvi delle piccole modifiche cosmetiche (tipo pulirlo, sporcarlo, raffreddarlo, riscaldarlo, dargli un sapore o cambiargli colore). In alternativa puoi dar vita a un'illusione non più grande di te stesso. Queste illusioni sono rozze, palesemente artefatte e di breve durata: non inganneranno nessuno, ma possono intrattenere o aiutarti a illustrare un concetto.",
  availableTo: [{ classId: 'mago' }],
};

const servitoreInvisibile: Spell = {
  id: 'mago.servitore-invisibile',
  name: 'Servitore Invisibile',
  level: 0,
  tags: ['continuato'],
  description:
    "Evochi un semplice costrutto invisibile con il solo scopo di trasportare oggetti. Ha Carico 3 e porta tutto ciò che gli porgi, ma non può raccogliere niente da solo. Ciò che il Servitore Invisibile trasporta sembra fluttuare a mezz'aria pochi passi dietro di te. Il costrutto svanisce immediatamente e lascia cadere il suo carico se subisce danno, ti perde di vista, lo bandisci o se evochi un altro Servitore Invisibile.",
  availableTo: [
    { classId: 'mago' },
    // Lista del Warlock (manuale r. 6211, Suppliche L0).
    { classId: 'warlock' },
  ],
};

// ============================================================================
// Livello 1 — il giocatore ne sceglie 3 alla creazione del PG
// ============================================================================

const allarme: Spell = {
  id: 'mago.allarme',
  name: 'Allarme',
  level: 1,
  tags: ['continuato'],
  description:
    "Al momento del lancio cammini descrivendo un ampio cerchio. Finché l'effetto permane, la magia ti avverte se una creatura oltrepassa il perimetro tracciato, e se stai dormendo ti sveglia dal sonno. L'incantesimo termina se decidi di porvi fine o se lo lanci nuovamente in un altro luogo. Mentre mantieni questo effetto, subisci un -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'druido' },
    // Tradizione Natura del Paladino (Elfo) attinge alla lista del Druido.
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
  ],
};

const ammaliarePersona: Spell = {
  id: 'mago.ammaliare-persona',
  name: 'Ammaliare Persona',
  level: 1,
  tags: ['continuato'],
  description:
    "Tocchi una persona (non un animale o un mostro) e questa ti considera un amico finché non gli dimostri il contrario, non subisce danni o non poni fine all'effetto. Mentre questo incantesimo è attivo, prendi -1 continuato a *lanciare incantesimi*. Al termine dell'effetto il bersaglio è consapevole di cosa è successo.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'chierico', tradizione: 'crepuscolo' },
    // Lista del Warlock (manuale r. 6211, L1).
    { classId: 'warlock' },
  ],
};

const comprensioneDeiLinguaggi: Spell = {
  id: 'mago.comprensione-dei-linguaggi',
  name: 'Comprensione dei Linguaggi',
  level: 1,
  tags: ['continuato'],
  description:
    "Divieni in grado di comprendere il significato letterale di qualsiasi linguaggio tu possa sentire o leggere. Se un testo è scritto con un alfabeto che non conosci, applica l'etichetta *lento* alla sua lettura. L'effetto non ti consente di decodificare un codice cifrato o di comprendere la natura di un sigillo magico. Mentre questo incantesimo è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [{ classId: 'mago' }, { classId: 'chierico', tradizione: 'illuminazione' }],
};

const contattareGliSpiriti: Spell = {
  id: 'mago.contattare-gli-spiriti',
  name: 'Contattare gli Spiriti',
  level: 1,
  tags: [],
  description:
    "Indica un tipo di spirito che intendi contattare (defunto, fatato, della natura, o lascia che decida il GM). La proiezione di uno di essi apparirà al tuo cospetto e ci potrai parlare senza limitazioni di linguaggio. L'apparizione svanisce dopo aver risposto a una tua singola domanda qualsiasi al meglio delle sue conoscenze. Non puoi contattare più volte lo stesso spirito, a meno che non sia lui stesso a volerlo.",
  availableTo: [{ classId: 'mago' }],
};

const dardoIncantato: Spell = {
  id: 'mago.dardo-incantato',
  name: 'Dardo Incantato',
  level: 1,
  tags: [],
  description:
    'Sprigioni proiettili di pura energia dalle tue dita, infliggendo 2d4 danni con etichetta *di magia materializzata* a un singolo bersaglio che puoi vedere.',
  availableTo: [{ classId: 'mago' }],
};

const individuazioneDelMagico: Spell = {
  id: 'mago.individuazione-del-magico',
  name: 'Individuazione del Magico',
  level: 1,
  tags: [],
  description:
    "Sviluppi per qualche momento un senso sintonizzato con la magia. Il GM ti dirà cosa di magico riesci a percepire nelle tue vicinanze e l'intensità dell'energia, ma non la sua natura.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'chierico', tradizione: 'illuminazione' },
    // Lista del Warlock (manuale r. 6211, L1).
    { classId: 'warlock' },
  ],
};

const invisibilita: Spell = {
  id: 'mago.invisibilita',
  name: 'Invisibilità',
  level: 1,
  tags: ['continuato'],
  description:
    "Un alleato che tocchi (incluso te stesso) diventa invisibile. L'effetto svanisce se decidi di dissolverlo o se lanci un incantesimo, oppure se il bersaglio compie una qualsiasi azione aggressiva.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'chierico', tradizione: 'crepuscolo' },
    { classId: 'druido' },
    // Tradizione Natura del Paladino (Elfo) attinge alla lista del Druido.
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
  ],
};

const telepatia: Spell = {
  id: 'mago.telepatia',
  name: 'Telepatia',
  level: 1,
  tags: ['continuato'],
  description:
    "Formi un legame telepatico con una persona consenziente che stai toccando, consentendovi così di comunicare a distanza con la sola forza del pensiero. L'effetto termina se decidi di porvi fine, se ti allontani di oltre qualche chilometro dal bersaglio, o se lanci nuovamente questo incantesimo. Mentre l'effetto è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'chierico', tradizione: 'crepuscolo' },
    // Lista del Warlock (manuale r. 6211, L1).
    { classId: 'warlock' },
  ],
};

// ============================================================================
// Livello 3 — accessibili dal livello 3 del PG Mago (D3)
// ============================================================================

const dissolviMagie: Spell = {
  id: 'mago.dissolvi-magie',
  name: 'Dissolvi Magie',
  level: 3,
  tags: [],
  description:
    'Scegli un incantesimo o un effetto magico che sia attivo entro distanza *vicino*: Dissolvi Magie lo fa a pezzi. Gli incantesimi minori terminano, le magie più potenti (a discrezione del GM) vengono solo ridotte o smorzate fino a quando rimani nei paraggi.',
  availableTo: [
    { classId: 'mago' },
    { classId: 'chierico', tradizione: 'guarigione' },
    { classId: 'chierico', tradizione: 'illuminazione' },
  ],
};

const immagineSpeculare: Spell = {
  id: 'mago.immagine-speculare',
  name: 'Immagine Speculare',
  level: 3,
  tags: ['continuato'],
  description:
    "Fai un passo di lato e un'emanazione illusoria con il tuo stesso aspetto compare accanto a te. Quando vieni attaccato, tira un d6: se fai 4 o più l'illusione viene colpita al tuo posto e l'effetto termina. L'incantesimo ha fine anche se lo lanci nuovamente o se usi la mossa *Mana* per ottenere prese.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'chierico', tradizione: 'valore' },
    { classId: 'paladino', tradizione: 'valore' },
  ],
};

const levitazione: Spell = {
  id: 'mago.levitazione',
  name: 'Levitazione',
  level: 3,
  tags: ['continuato'],
  description:
    "Una creatura consenziente che stai toccando (incluso te stesso) viene liberata dal giogo della gravità. Finché mantieni attivo l'incantesimo, essa può muoversi verso l'alto o il basso come se stesse camminando. Per gli spostamenti orizzontali, invece, deve trovare qualcosa da usare per darsi la spinta. In ogni caso il GM terrà conto della sua assenza di peso. Mentre l'effetto è attivo, prendi un -1 continuato a *lanciare incantesimi*.",
  availableTo: [{ classId: 'mago' }],
};

const mimesi: Spell = {
  id: 'mago.mimesi',
  name: 'Mimesi',
  level: 3,
  tags: ['continuato'],
  description:
    "Assumi la forma di una persona che stai toccando al momento del lancio. L'aspetto fisico e la voce corrispondono esattamente ai suoi, ma il tuo comportamento potrebbe sempre tradirti. L'effetto persiste finché non subisci danno, scegli di tornare alla tua forma normale, o usi la mossa *Mana* per ottenere prese. Mentre questo incantesimo è attivo non puoi usare le tue mosse di classe.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'chierico', tradizione: 'crepuscolo' },
    { classId: 'warlock' },
  ],
};

const oscurita: Spell = {
  id: 'mago.oscurita',
  name: 'Oscurità',
  level: 3,
  tags: ['continuato'],
  description:
    "Tenebre sovrannaturali si propagano da un punto che puoi vedere, riempiendo l'area circostante fino a portata *lunga*. Mentre questo incantesimo è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'chierico', tradizione: 'crepuscolo' },
    { classId: 'warlock' },
  ],
};

const pallaDiFuoco: Spell = {
  id: 'mago.palla-di-fuoco',
  name: 'Palla di Fuoco',
  level: 3,
  tags: [],
  description:
    'Evochi una potente sfera di fiamme che avvolge un bersaglio entro distanza *lontano* e tutti coloro che si trovano entro portata *lunga* da esso. Chi viene colpito subisce 2d4 danni con etichette *infuocato* e *ignora armatura*.',
  availableTo: [{ classId: 'mago' }],
};

const sonno: Spell = {
  id: 'mago.sonno',
  name: 'Sonno',
  level: 3,
  tags: [],
  description:
    "1d4 nemici entro distanza *vicino* che siano in grado di dormire cadono in un sonno ordinario. Il GM ti dirà chi viene colpito. Le vittime si sveglieranno per cause naturali (luci o rumori forti, scosse, dolore) o dopo qualche ora di riposo: nel frastuono della battaglia, quindi, l'incantesimo provoca solo un momento di sbandamento ai bersagli prima che si riprendano.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'druido' },
    // Tradizione Natura del Paladino (Elfo) attinge alla lista del Druido.
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
  ],
};

/**
 * 3 trucchetti del Mago, tutti **auto-aggiunti** al PG quando seleziona la
 * classe Mago (manuale r. 4087: «*tutti i trucchetti della lista del Mago*»).
 */
export const magoCantrips: readonly Spell[] = [luce, prestidigitazione, servitoreInvisibile];

/**
 * 8 incantesimi di livello 1 del Mago. Il giocatore ne sceglie **3** alla
 * creazione del PG (manuale r. 4087: «*Inizi il gioco con tre incantesimi
 * di primo livello*»).
 */
export const magoLevel1Spells: readonly Spell[] = [
  allarme,
  ammaliarePersona,
  comprensioneDeiLinguaggi,
  contattareGliSpiriti,
  dardoIncantato,
  individuazioneDelMagico,
  invisibilita,
  telepatia,
];

/**
 * 7 incantesimi di livello 3 del Mago. Accessibili dal livello-PG 3 via
 * regola *Corpus Arcano* ricorrente (FI-33, `grantsSpells.on-each-level-up`).
 */
export const magoLevel3Spells: readonly Spell[] = [
  dissolviMagie,
  immagineSpeculare,
  levitazione,
  mimesi,
  oscurita,
  pallaDiFuoco,
  sonno,
];

// ============================================================================
// Livello 5 — accessibili dal livello 5 del PG Mago (D3)
// ============================================================================

const contattareAltriPiani: Spell = {
  id: 'mago.contattare-altri-piani',
  name: 'Contattare Altri Piani',
  level: 5,
  tags: ['continuato'],
  description:
    "Invii una richiesta d'udienza verso un altro piano di esistenza, specificando chi vorresti contattare (indica un luogo, un tipo di creatura, un nome o un titolo). Tu e la creatura contattata potrete parlarvi senza limitazioni di linguaggio. La comunicazione può essere interrotta in ogni momento sia da te che dall'altra creatura, e comunque termina quando usi la mossa *Mana* per ottenere prese. Finché l'effetto permane, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'druido' },
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
    { classId: 'warlock' },
  ],
};

const evocareMostri: Spell = {
  id: 'mago.evocare-mostri',
  name: 'Evocare Mostri',
  level: 5,
  tags: ['continuato'],
  description:
    "Un mostro appare e ti aiuta *senza discutere* i tuoi ordini al meglio delle sue facoltà. Consideralo come se fosse un gregario di livello 2, senza alcun Costo. Ha un modificatore di +1 a tutte le Caratteristiche, 1 PF, Carico 6, e infligge 1d6 danni. Il mostro ottiene anche 1d4+2 tratti a tua scelta tra i seguenti:\n\n• ha un +2 invece che un +1 a una Caratteristica (un +3 se scegli quest'opzione due volte);\n• non è spericolato;\n• infligge +1 danni (puoi scegliere quest'opzione fino a due volte);\n• il suo legame con questo piano è forte: ha +2 PF per ciascun tuo livello da incantatore (al massimo +20 PF);\n• descrivi una capacità utile che speri che abbia (puoi scegliere quest'opzione due volte);\n• ha un'Abilità a Rango 1 (oppure, se scegli quest'opzione due volte, due a Rango 1 o una a Rango 2).\n\nSulla base dei tratti che hai scelto, il GM ti dirà che mostro appare e come si concretizzano le eventuali capacità utili che hai indicato. La creatura resta su questo piano fino a quando non muore o non la bandisci. Mentre l'incantesimo è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [{ classId: 'mago' }],
};

const fulminictus: Spell = {
  id: 'mago.fulminictus',
  name: 'Fulminictus',
  level: 5,
  tags: [],
  description:
    'Scateni un fulmine su un bersaglio a gittata *lontano*, che rimbalza anche su coloro che si trovano entro portata *lunga* da esso. Chi viene colpito subisce 2d6 danni con etichette *elettrico* e *ignora armatura*. Il bersaglio principale, inoltre, viene stordito dalla scarica.',
  availableTo: [{ classId: 'mago' }],
};

const gemmaDellAnima: Spell = {
  id: 'mago.gemma-dell-anima',
  name: "Gemma dell'Anima",
  level: 5,
  tags: [],
  description:
    "Intrappoli l'anima di una creatura morente in una gemma. Essa è consapevole della sua prigionia e di cosa è accaduto, ma può essere manipolata con incantesimi, *parlamentando*, o in altri modi che possano ragionevolmente influenzare un'anima. Tutte le mosse effettuate contro la creatura intrappolata ottengono +1 al tiro. Puoi liberare l'anima quando vuoi, ma non potrai mai più intrappolarla in seguito.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'chierico', tradizione: 'negromanzia' },
    { classId: 'paladino', tradizione: 'negromanzia' },
  ],
};

const metamorfosi: Spell = {
  id: 'mago.metamorfosi',
  name: 'Metamorfosi',
  level: 5,
  tags: ['continuato'],
  description:
    "Cambi completamente la tua forma o quella di una creatura che tocchi. Il nuovo aspetto permarrà finché non lanci un qualsiasi altro incantesimo o decidi di porre fine all'effetto. Descrivi la nuova forma che hai creato, compresi eventuali cambiamenti agli attributi (Caratteristiche, Armatura, Danno, ecc.), capacità utili o particolari debolezze. Il GM, in base alla tua descrizione, sceglierà una o più delle seguenti opzioni:\n\n• la forma è instabile o temporanea;\n• anche la mente del bersaglio viene alterata;\n• la forma ha una debolezza o un beneficio non previsto;\n• l'equipaggiamento mal si adatta alla nuova forma.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'chierico', tradizione: 'crepuscolo' },
    { classId: 'chierico', tradizione: 'negromanzia' },
    { classId: 'paladino', tradizione: 'negromanzia' },
  ],
};

const telecinesi: Spell = {
  id: 'mago.telecinesi',
  name: 'Telecinesi',
  level: 5,
  tags: ['continuato'],
  description:
    "Manipoli con la forza del pensiero un oggetto inanimato entro distanza *vicino*, purché tu sia in grado di vederlo e che non sia indossato o trasportato da nessuno. Puoi compiere sull'oggetto o con esso azioni anche complesse. Se una di tali azioni richiede di tirare+**FOR**, tira+**CAR**, se richiede **DES**, usa **INT**, e, al posto di **COS**, usa **SAG**. Finché l'effetto permane puoi cambiare liberamente bersaglio, ma sei in grado di manipolare un solo oggetto per volta. L'incantesimo termina se decidi di porvi fine o quando usi la mossa *Mana* per ottenere prese. Mentre l'effetto è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [{ classId: 'mago' }, { classId: 'chierico', tradizione: 'crepuscolo' }],
};

const visioneDelVero: Spell = {
  id: 'mago.visione-del-vero',
  name: 'Visione del Vero',
  level: 5,
  tags: ['continuato'],
  description:
    "La tua vista non è offuscata da illusioni e falsità, magiche o mondane, e vedi la vera natura delle cose e ciò che è nascosto ai sensi comuni. Il GM ti descriverà ciò che hai davanti tenendo conto di questa tua facoltà. L'incantesimo termina se decidi di porvi fine o se usi la mossa *Mana* per ottenere prese. Mentre l'effetto è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'chierico', tradizione: 'guarigione' },
    { classId: 'chierico', tradizione: 'illuminazione' },
  ],
};

/** 7 incantesimi di livello 5 del Mago (D3). */
export const magoLevel5Spells: readonly Spell[] = [
  contattareAltriPiani,
  evocareMostri,
  fulminictus,
  gemmaDellAnima,
  metamorfosi,
  telecinesi,
  visioneDelVero,
];

// ============================================================================
// Livello 7 — accessibili dal livello 7 del PG Mago (D4)
// ============================================================================

const barrieraMagica: Spell = {
  id: 'mago.barriera-magica',
  name: 'Barriera Magica',
  level: 7,
  tags: ['continuato'],
  description:
    "Evochi una barriera di energia magica che si estende fino a gittata *lunga* da dove ti trovi, proteggendo chiunque sia al suo interno. Gli attacchi di tipo fisico o elementale che provengano dall'esterno vengono bloccati, ma le creature possono attraversarla liberamente. Normalmente la barriera svanisce quando assorbe un qualsiasi attacco, ma puoi decidere di spendere una presa di *Mana* affinché non collassi. Puoi anche decidere di porre termine all'incantesimo in qualunque momento. Fintanto che la barriera è attiva, prendi -1 continuato a *lanciare incantesimi*.\n\nTieni presente che la barriera protegge solo da attacchi che provengono dal suo esterno: il fendente di quell'enorme orco di fronte a te è una minaccia che l'incantesimo non può bloccare. E quando il barile di pece, che si trova all'interno della barriera a un passo da Durga esplode, beh, il danno non viene annullato. Infine, ricorda che solo gli effetti fisici ed elementali (fuoco, frecce, suono, gas, ecc.) vengono bloccati, mentre effetti di altro tipo non lo sono: il negromante può evocare la sua orda accanto a te, e lo stregone è in grado di ammaliarti con il suo sguardo senza che la barriera si attivi.",
  availableTo: [{ classId: 'mago' }],
};

const dominare: Spell = {
  id: 'mago.dominare',
  name: 'Dominare',
  level: 7,
  tags: ['continuato'],
  description:
    "Proietti la tua mente dentro quella di una creatura senziente che stai toccando e ottieni 1d4 prese. Spendi 1 presa per far fare al bersaglio una delle seguenti azioni:\n\n• dire qualche parola a tua scelta;\n• darti qualcosa che tiene in mano;\n• fare un attacco coordinato contro un bersaglio a tua scelta;\n• rispondere sinceramente a una domanda.\n\nSe il bersaglio subisce danni, perdi 1 presa. L'effetto termina quando non hai più prese, se lanci un qualsiasi altro incantesimo, o se decidi di porvi fine.",
  availableTo: [{ classId: 'mago' }, { classId: 'chierico', tradizione: 'crepuscolo' }],
};

const gabbia: Spell = {
  id: 'mago.gabbia',
  name: 'Gabbia',
  level: 7,
  tags: ['continuato'],
  description:
    "Imprigioni una creatura che puoi vedere in un parallelepipedo di tenebra assoluta poco più grande di essa. Niente (materia, magia, luce o suono) può entrare o uscire dall'involucro, nemmeno attraverso il teletrasporto o lo spostamento planare. Il bersaglio mantiene comunque una limitata capacità d'azione entro l'angusto spazio in cui si trova, e tu sei in grado di comunicarci telepaticamente senza limitazioni di linguaggio (se la creatura è senziente). La Gabbia svanisce se lanci un incantesimo, la perdi di vista, o la dissolvi.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'chierico', tradizione: 'valore' },
    { classId: 'paladino', tradizione: 'valore' },
  ],
};

const innesco: Spell = {
  id: 'mago.innesco',
  name: 'Innesco',
  level: 7,
  tags: ['continuato'],
  description:
    "Scegli un incantesimo di livello 5 o inferiore che puoi lanciare e descrivi una condizione di attivazione usando al massimo 7 parole: il suo effetto rimane latente fino a quando non decidi di attivarlo o si verifica la condizione d'innesco, qualunque delle due cose accada prima. Non devi tirare per lanciare l'incantesimo trattenuto: ha semplicemente effetto. Puoi avere un solo Innesco attivo alla volta: se ne lanci un altro, il nuovo incantesimo sostituirà il precedente.",
  availableTo: [{ classId: 'mago' }, { classId: 'chierico', tradizione: 'guarigione' }],
};

const nubeMortale: Spell = {
  id: 'mago.nube-mortale',
  name: 'Nube Mortale',
  level: 7,
  tags: ['continuato'],
  description:
    "Una nebbia proveniente dai Neri Cancelli della Morte permea questo mondo, riempiendo la zona intorno a te fino a portata *lunga*. Quando un nemico subisce almeno 1 danno mentre si trova nella Nube Mortale, riceve 1d6 danni aggiuntivi con etichette *ignora armatura* e *necrotico*. L'incantesimo termina quando decidi di porvi fine, o se usi nuovamente la mossa *Mana* per ottenere prese. Finché l'effetto è attivo, prendi un -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'chierico', tradizione: 'negromanzia' },
    { classId: 'paladino', tradizione: 'negromanzia' },
  ],
};

const parolaDelRitorno: Spell = {
  id: 'mago.parola-del-ritorno',
  name: 'Parola del Ritorno',
  level: 7,
  tags: ['continuato'],
  description:
    "Dedichi qualche minuto a creare un'ancora spaziale in un luogo e a legarvi una parola. La prima volta che la pronuncerai, tu e gli alleati che ti sono vicini verrete immediatamente riportati nel posto in cui hai posizionato l'ancora. L'incantesimo svanisce una volta attivato, o se usi una qualsiasi forma di teletrasporto (volontariamente o meno). Puoi avere una sola ancora attiva per volta.",
  availableTo: [
    { classId: 'mago' },
    { classId: 'druido' },
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
  ],
};

const separazione: Spell = {
  id: 'mago.separazione',
  name: 'Separazione',
  level: 7,
  tags: ['continuato'],
  description:
    "Indica un arto o un'appendice non essenziale per la sopravvivenza o la deambulazione primaria di una creatura che puoi vedere, tipo un braccio, una gamba (se ne ha più di due), un tentacolo o un'ala. Il controllo sull'appendice viene reciso magicamente, senza causare danno ma provocando un forte dolore. Ciò può inibire alcune capacità della vittima, impedendo ad esempio il volo a una creatura alata o a un toro di incornarti. L'incantesimo ha termine se decidi di porvi fine o quando usi la mossa *Mana* per ottenere prese. Mentre l'effetto è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [{ classId: 'mago' }],
};

// ============================================================================
// Livello 9 — accessibili dal livello 9 del PG Mago (D4)
// ============================================================================

const celare: Spell = {
  id: 'mago.celare',
  name: 'Celare',
  level: 9,
  tags: ['continuato'],
  description:
    "Una creatura consenziente (incluso te stesso) o un oggetto inanimato che stai toccando diventa invisibile, non individuabile con mezzi magici o mondani, smette di invecchiare e non percepisce lo scorrere del tempo. Poni una condizione chiara e univoca, al verificarsi della quale l'effetto di dissolverà. L'incantesimo termina anche se il bersaglio subisce danni o se decidi di porvi fine (cosa che *non puoi fare* se hai celato te stesso).",
  availableTo: [{ classId: 'mago' }],
};

const evocazioneInfallibile: Spell = {
  id: 'mago.evocazione-infallibile',
  name: 'Evocazione Infallibile',
  level: 9,
  tags: [],
  description:
    "Pronuncia il vero nome di una creatura o indicane genericamente un tipo. L'individuo specifico, o un esemplare del tipo che hai indicato, viene teletrasportato fisicamente al tuo cospetto ovunque si trovi in questo mondo. Ciò non significa, però, che sia ben disposto nei tuoi confronti! Magie estremamente potenti potrebbero proteggere il bersaglio dalla tua evocazione: in tal caso il GM ti dirà onestamente la ragione per cui l'incantesimo ha fallito e se la creatura ha percepito il tuo tentativo.",
  availableTo: [{ classId: 'mago' }],
};

const presagio: Spell = {
  id: 'mago.presagio',
  name: 'Presagio',
  level: 9,
  tags: ['continuato'],
  description:
    "Descrivi una circostanza: quando si verificherà, non importa dove o quando, lo verrai a sapere. Se vuoi, potrai anche vivere in prima persona l'evento come se fossi presente, pur non essendoci fisicamente, ma non potrai interagire o essere percepito. L'incantesimo termina quando la circostanza si verifica o se lo lanci nuovamente, nel qual caso il nuovo Presagio sostituirà il precedente.",
  availableTo: [{ classId: 'mago' }],
};

const rifugio: Spell = {
  id: 'mago.rifugio',
  name: 'Rifugio',
  level: 9,
  tags: ['continuato'],
  description:
    "Materializzi la magia stessa per realizzare un edificio della foggia che preferisci: la struttura può essere piccola come una capanna o grande come un castello. Finché ti trovi al suo interno sei cosciente di tutto ciò che accade tra le sue mura e puoi spendere 1 presa di *Mana* per modificarne l'aspetto, ripararla, espellere un intruso o impedire a qualcuno di uscirne. L'edificio si smaterializza quando decidi di porre termine all'incantesimo, se materializzi un altro Rifugio, o se usi la mossa *Mana* per recuperare prese.",
  availableTo: [{ classId: 'mago' }],
};

/**
 * **Description completa allineata al manuale V1** (r. 6594, gap iniziale
 * chiuso 2026-05-15 con aggiornamento manuale).
 */
const tempestaArcana: Spell = {
  id: 'mago.tempesta-arcana',
  name: 'Tempesta Arcana',
  level: 9,
  tags: ['continuato'],
  description:
    "Manipoli le energie atmosferiche per creare un fenomeno innaturale a tua scelta (piogge corrosive, venti di forza inimmaginabile, nubi di energia arcana, ecc.). Puoi decidere dove la Tempesta ha luogo, purché sia entro il tuo campo visivo, ma è il GM a descriverne gli effetti. Finché l'incantesimo perdura, puoi variare l'entità e l'ampiezza del fenomeno a tuo piacimento. L'effetto ha termine se compi una qualsiasi altra azione che non sia parlare o muoverti lentamente.",
  availableTo: [{ classId: 'mago' }],
};

/**
 * 7 incantesimi di livello 7 del Mago (D4). Includono *Separazione* che
 * il parsing della tabella indice del manuale (r. 6216) classificava
 * erroneamente come L9 — la description canonica (r. 6569) e il PDF
 * inviato 2026-05-13 confermano L7.
 */
export const magoLevel7Spells: readonly Spell[] = [
  barrieraMagica,
  dominare,
  gabbia,
  innesco,
  nubeMortale,
  parolaDelRitorno,
  separazione,
];

/** 5 incantesimi di livello 9 del Mago (D4). */
export const magoLevel9Spells: readonly Spell[] = [
  celare,
  evocazioneInfallibile,
  presagio,
  rifugio,
  tempestaArcana,
];

/** Tutti gli spell del Mago trascritti (cantrips + L1 + L3 + L5 + L7 + L9). */
export const magoSpells: readonly Spell[] = [
  ...magoCantrips,
  ...magoLevel1Spells,
  ...magoLevel3Spells,
  ...magoLevel5Spells,
  ...magoLevel7Spells,
  ...magoLevel9Spells,
];
